import React, { useEffect, useMemo, useRef, useState } from "react";
import Globe from "react-globe.gl";
import { COUNTRY_COORDS } from "@/lib/countryCoords";

export default function WorldGlobe({ countries, onSelect, selectedCountry }) {
  const globeRef = useRef(null);
  const wrapperRef = useRef(null);
  const [dim, setDim] = useState({ w: 800, h: 500 });
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const e = entries[0];
      setDim({ w: e.contentRect.width, h: e.contentRect.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const points = useMemo(() => {
    return countries
      .map((c) => {
        const coords = COUNTRY_COORDS[c.iso_3166_1];
        if (!coords) return null;
        const size = Math.min(0.4, Math.log10(c.stationcount + 1) * 0.08);
        return {
          name: c.name,
          iso: c.iso_3166_1,
          lat: coords[0],
          lng: coords[1],
          size,
          count: c.stationcount,
          color:
            selectedCountry === c.name
              ? "#22c55e"
              : c.stationcount > 500
              ? "#06b6d4"
              : c.stationcount > 100
              ? "#ec4899"
              : "#eab308",
        };
      })
      .filter(Boolean);
  }, [countries, selectedCountry]);

  useEffect(() => {
    if (!globeRef.current) return;
    const controls = globeRef.current.controls();
    controls.autoRotate = true;
    controls.autoRotateSpeed = 0.35;
    controls.enableZoom = true;
    globeRef.current.pointOfView({ lat: 20, lng: 0, altitude: 2.4 }, 0);
  }, []);

  return (
    <div ref={wrapperRef} data-testid="world-globe" className="w-full h-full relative">
      <Globe
        ref={globeRef}
        width={dim.w}
        height={dim.h}
        backgroundColor="rgba(0,0,0,0)"
        globeImageUrl="//unpkg.com/three-globe/example/img/earth-dark.jpg"
        bumpImageUrl="//unpkg.com/three-globe/example/img/earth-topology.png"
        atmosphereColor="#06b6d4"
        atmosphereAltitude={0.18}
        pointsData={points}
        pointLat="lat"
        pointLng="lng"
        pointColor="color"
        pointAltitude="size"
        pointRadius={0.9}
        pointResolution={6}
        pointLabel={(d) =>
          `<div style="background:#0a0a0a;border:1px solid #1a1a1a;padding:6px 10px;font-family:'Azeret Mono',monospace;font-size:10px;letter-spacing:0.1em;text-transform:uppercase;color:#f8fafc"><div style="color:#06b6d4">${d.name}</div><div style="color:#94a3b8;margin-top:2px">${d.count} stations · click to open</div></div>`
        }
        onPointClick={(d) => onSelect(d.name)}
        onPointHover={setHovered}
      />
      {/* Overlay HUD */}
      <div className="absolute top-4 left-4 pointer-events-none">
        <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-cyan-400">TUNE THE WORLD</div>
        <div className="font-heading font-black text-3xl md:text-5xl leading-none tracking-tight text-white mt-1">
          Pick a country<span className="text-cyan-400">.</span>
        </div>
        <div className="font-body text-sm text-slate-500 mt-2 max-w-md">
          Spin the globe. Click any dot to load its live radio stations.
        </div>
      </div>
      <div className="absolute bottom-4 right-4 pointer-events-none flex items-center gap-3">
        <Legend color="#eab308" label="< 100" />
        <Legend color="#ec4899" label="100+" />
        <Legend color="#06b6d4" label="500+" />
      </div>
    </div>
  );
}

function Legend({ color, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="w-2 h-2 rounded-full" style={{ background: color }} />
      <span className="font-mono-tech text-[9px] uppercase tracking-widest text-slate-500">{label}</span>
    </div>
  );
}
